import { userSelector } from 'common/globalComponents/selectors';
import Breadcrumbs from 'components/Common/Breadcrumb';
import Loader from 'components/Loader';
import AssetImageUploader from 'containers/blog/BlogList/components/AssetImageUploader';
import { useFormik } from 'formik';
import useRequest from 'hooks/useRequest';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { Button, Card, CardBody, Col, Container, Form, FormFeedback, FormGroup, Input, Label, Row } from 'reactstrap';
import { createStructuredSelector } from 'reselect';
import { GENDER_ARRAY, LIFESTYLE_ARRAY, MODULE_TYPE } from 'utils/constants';
import { SMKLinks } from 'utils/links';
import * as Yup from 'yup';
import { FOOD_ITEM_UNITS } from '../constants';
import MultipleItems from './components/multipleItems';
import CaloriesTable from './components/caloriesTable';
import { Calories_Data } from './constants';

const stateSelector = createStructuredSelector({
  user: userSelector,
});

const AddUpdateDietPlan = () => {
  const history = useHistory();
  const { id: dietPlanId } = useParams();

  toast.configure();

  const { user } = useSelector(stateSelector);

  const [dietPlanDetails, setDietPlanDetails] = useState();

  const buildAgeGroupOptions = (data) => {
    const map = new Map();

    data.forEach((item) => {
      const key = `${item.ageGroupMin}-${item.ageGroupMax}`;

      if (!map.has(key)) {
        let label = '';

        const minMonths = item.ageGroupMin;
        const maxMonths = item.ageGroupMax;

        // both ≤ 18 months → months only
        if (maxMonths <= 18) {
          label = `${minMonths} Months to ${maxMonths} Months`;
        }
        // crosses 18 months → months to years
        else if (minMonths < 18 && maxMonths > 18) {
          const maxYears = Math.floor(maxMonths / 12);
          label = `${minMonths} Months to ${maxYears} Years`;
        }
        // both > 18 months → years only
        else {
          const minYears = Math.floor(minMonths / 12);
          const maxYears = Math.floor(maxMonths / 12);
          label = `${minYears} Years to ${maxYears} Years`;
        }

        map.set(key, {
          label,
          value: key,
          minMonths,
          maxMonths,
        });
      }
    });

    return Array.from(map.values()).sort((a, b) => a.minMonths - b.minMonths);
  };

  const AGE_GROUP_OPTIONS = buildAgeGroupOptions(Calories_Data);

  const AMOUNT_UNITS = [...new Set(Object.values(FOOD_ITEM_UNITS)?.flat(2) ?? [])]?.flat(2)?.map((e) => ({
    label: e?.toUpperCase(),
    value: e,
  }));

  const [getDietPlanRequest, { loading: getDietPlanLoading }] = useRequest({
    method: 'GET',
    url: `/nutrition/diet-plan/admin/${dietPlanId}`,
    onSuccess: (data) => {
      setDietPlanDetails(data);
    },
    onError: (err) => {
      toast.error(err?.message?.message || err?.message);
    },
  });

  const [addDietPlanRequest, { loading: addDietPlanLoading }] = useRequest({
    method: 'POST',
    url: '/nutrition/diet-plan/admin',
    onSuccess: (data) => {
      const url = SMKLinks.DIET_PLAN_LIST;
      history.replace(url);
      toast.success('Success! Diet Plan has been added.');
    },
    onError: (err) => {
      toast.error(err?.message?.message || err?.message);
    },
  });

  const [updateDietPlanRequest, { loading: updateDietPlanLoading }] = useRequest({
    method: 'PUT',
    url: `/nutrition/diet-plan/admin/${dietPlanId}`,
    onSuccess: () => {
      toast.success('Success! Diet Plan has been updated.');
      window.location.reload();
    },
    onError: (err) => {
      toast.error(err?.message?.message || err?.message);
    },
  });

  const editInitialValues = () => {
    return {
      active: dietPlanDetails?.active,
      deleted: dietPlanDetails?.deleted,
      name: dietPlanDetails?.name,
      ageGroupMin: dietPlanDetails?.ageGroupMin || 0,
      ageGroupMax: dietPlanDetails?.ageGroupMax || 0,
      lifestyle: dietPlanDetails?.lifestyle || '',
      gender: dietPlanDetails?.gender,
      image: dietPlanDetails?.image,

      items: !isEmpty(dietPlanDetails?.items)
        ? dietPlanDetails?.items?.map((e) => ({
            amount: e?.amount,
            amountUnit: e?.amountUnit,
            calories: e?.calories,
            ...(!isEmpty(e?.recipe) ? { recipeId: e?.recipe?.id } : {}),
            ...(!isEmpty(e?.foodItem) ? { foodItemId: e?.foodItem?.id } : {}),
            isFoodItem: !isEmpty(e?.foodItem),
          }))
        : null,
    };
  };

  const getInitialValues = () => ({
    active: true,
    deleted: false,
    name: '',
    ageGroupMin: 0,
    ageGroupMax: 0,
    lifestyle: '',
    gender: 0,
    image: null,
    items: null,
  });

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: false,
    initialValues: isEmpty(dietPlanId) ? { ...getInitialValues() } : { ...editInitialValues() },
    onSubmit: async (values) => {
      const data = {
        active: values?.active,
        deleted: values?.deleted,
        name: values?.name,
        gender: values?.gender,
        ageGroupMin: values?.ageGroupMin,
        ageGroupMax: values?.ageGroupMax,
        lifestyle: values?.lifestyle,
        imageId: values?.image?.id || null,
        isPrivate: true,
        language: false,
        patientId: null,
      };
      const payload = {
        body: data,
      };
      if (isEmpty(dietPlanId)) {
        addDietPlanRequest(payload);
      } else {
        updateDietPlanRequest(payload);
      }
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Please provide name'),
      gender: Yup.string().required('Please provide gender'),
    }),
  });

  useEffect(() => {
    if (!isEmpty(dietPlanId)) {
      getDietPlanRequest();
    }
  }, [dietPlanId]);

  return (
    <div className="page-content">
      <Loader isActive={addDietPlanLoading || getDietPlanLoading || updateDietPlanLoading} />
      <Container fluid>
        <Breadcrumbs title="Diet Plan" breadcrumbItem={`${isEmpty(dietPlanId) ? 'Add' : 'Edit'}  Diet Plan`} />
        <Form onSubmit={formik.handleSubmit}>
          <Row>
            <Col md={9}>
              <Card className="mb-4">
                <CardBody>
                  <Row className="align-items-center">
                    <Col md={12}>
                      <FormGroup className="mb-4">
                        <Label for="name" className="form-label  ">
                          Name <span className="text-danger">*</span>
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          className="form-control"
                          placeholder="Enter Name..."
                          invalid={!!(formik.touched.name && formik.errors.name)}
                          {...formik.getFieldProps('name')}
                        />
                        <FormFeedback className="d-block">{formik.errors.name}</FormFeedback>
                      </FormGroup>
                    </Col>

                    <Col md={4}>
                      <FormGroup className="mb-4">
                        <Label for="gender" className="form-label">
                          Gender <span className="text-danger">*</span>
                        </Label>
                        <Select
                          id="gender"
                          options={GENDER_ARRAY}
                          value={GENDER_ARRAY?.find((e) => e?.value === formik.values.gender) || null}
                          onChange={(e) => formik.setFieldValue('gender', e?.value)}
                        />
                        <FormFeedback className="d-block">{formik.errors.gender}</FormFeedback>
                      </FormGroup>
                    </Col>

                    <Col md={4}>
                      <FormGroup className="mb-4">
                        <Label for="ageGroupMin">Age Group</Label>
                        <Select
                          id="ageGroup"
                          options={AGE_GROUP_OPTIONS}
                          value={
                            AGE_GROUP_OPTIONS.find(
                              (e) =>
                                e.minMonths === formik.values.ageGroupMin && e.maxMonths === formik.values.ageGroupMax
                            ) || null
                          }
                          onChange={(e) => {
                            if (!e) {
                              formik.setFieldValue('ageGroupMin', null);
                              formik.setFieldValue('ageGroupMax', null);
                              return;
                            }

                            formik.setFieldValue('ageGroupMin', e.minMonths);
                            formik.setFieldValue('ageGroupMax', e.maxMonths);
                          }}
                          isClearable
                          menuPlacement="auto"
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999,
                            }),
                            menuList: (base) => ({
                              ...base,
                              maxHeight: 300,
                              overflowY: 'auto',
                              backgroundColor: 'white',
                            }),
                          }}
                        />
                        <FormFeedback className="d-block">{formik.errors.ageGroupMin}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup className="mb-4">
                        <div>
                          <Label for="lifestyle">Life Style</Label>
                        </div>
                        {LIFESTYLE_ARRAY?.map((option, i) => (
                          <div key={`lifeStyle_${i}`} className="form-check d-inline-block me-4">
                            <input
                              type="radio"
                              id={`lifeStyle_${option?.value}`}
                              name="lifestyle"
                              className="form-check-input"
                              checked={formik.values.lifestyle === option?.value}
                              onChange={() => formik.setFieldValue('lifestyle', option?.value)}
                            />
                            <label className="form-check-label" htmlFor={`lifeStyle_${option?.value}`}>
                              {option?.label}
                            </label>
                          </div>
                        ))}
                        <FormFeedback className="d-block">{formik.errors.lifestyle}</FormFeedback>
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
              {dietPlanId && (
                <MultipleItems
                  unitsList={AMOUNT_UNITS}
                  formik={formik}
                  keyName={'items'}
                  dietPlanDetails={dietPlanDetails}
                />
              )}
            </Col>
            <Col md={3}>
              <Card>
                <CardBody>
                  <div>
                    {user?.entityPermissions?.nutrition_diet_plan?.publish && dietPlanId && (
                      <Button
                        type="button"
                        color={dietPlanDetails?.active ? 'danger' : 'success'}
                        className="mb-3 w-100"
                        onClick={() => {
                          const action = dietPlanDetails?.active ? 'unpublish' : 'publish';
                          const isConfirm = confirm(`Are you sure you want to ${action} this item?`);

                          if (isConfirm) {
                            formik.setFieldValue('active', !dietPlanDetails?.active);
                            formik.setFieldValue('deleted', false);
                            formik.handleSubmit();
                          }
                        }}>
                        {dietPlanDetails?.active ? 'Unpublish' : 'Publish'}
                      </Button>
                    )}

                    <Button type="submit" color="primary" className="mb-3 w-100">
                      {dietPlanId ? 'Save' : 'Add'}
                    </Button>

                    <Button
                      className="w-100"
                      color="light"
                      onClick={() => {
                        history.replace(SMKLinks.DIET_PLAN_LIST);
                      }}>
                      Cancel
                    </Button>
                  </div>
                </CardBody>
              </Card>
              {/* <Card>
                <CardBody>
                  <Row className="align-items-center">
                    <Col md={12}>
                      <CardTitle className="mb-3">Active </CardTitle>
                      {[true, false].map((option, i) => (
                        <div key={`active_${i}`} className="form-check mb-3 d-inline-block me-4">
                          <input
                            type="radio"
                            id={`active_${option}`}
                            name="active"
                            className="form-check-input"
                            checked={formik.values.active === option}
                            onChange={() => formik.setFieldValue('active', option)}
                          />
                          <label className="form-check-label" htmlFor={`active_${option}`}>
                            {option ? 'Yes' : 'No'}
                          </label>
                        </div>
                      ))}
                    </Col>

                    <Col md={12}>
                      <CardTitle className="mb-3">Delete </CardTitle>
                      {[true, false].map((option, i) => (
                        <div key={`deleted_${i}`} className="form-check mb-3 d-inline-block me-4">
                          <input
                            type="radio"
                            id={`deleted_${option}`}
                            name="deleted"
                            className="form-check-input"
                            checked={formik.values.deleted === option}
                            onChange={() => formik.setFieldValue('deleted', option)}
                          />
                          <label className="form-check-label" htmlFor={`deleted_${option}`}>
                            {option ? 'Yes' : 'No'}
                          </label>
                        </div>
                      ))}
                    </Col>
                  </Row>
                </CardBody>
              </Card> */}

              <AssetImageUploader
                forType={MODULE_TYPE.RECIPE}
                formik={formik}
                fieldName={'image'}
                label={'Select Image'}
              />
              {dietPlanId && !isEmpty(dietPlanDetails) && (
                <CaloriesTable
                  gender={dietPlanDetails?.gender}
                  lifestyle={dietPlanDetails?.lifestyle}
                  ageGroupMin={dietPlanDetails?.ageGroupMin}
                  ageGroupMax={dietPlanDetails?.ageGroupMax}
                />
              )}
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default AddUpdateDietPlan;
